import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';

import { api } from './api';
import { login, logout } from './msal';
import { MeetingRecorder, NoSystemAudioError, acquireCapture } from './recorder';
import { getTodaysMeetings, findCurrentMeeting } from './graph';
import { checkCapabilities } from './browser';
import { useCoachPip, CoachOverlay } from './CoachPip';
import Info from './Info';
import Speakers from './Speakers';
import { formatDuration, formatSize, formatDate } from './format';
import { mdToHtml } from './markdown';

function meetingTime(m) {
  if (!m || !m.start) return '';
  return new Date(m.start).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

// Teilnehmer als eine Zeile. Bei großen Runden nur die ersten Namen und die Anzahl
// der übrigen – sonst sprengt eine 20-Personen-Einladung die Auswahlliste.
function attendeeLine(m, max = 4) {
  const names = (m && m.attendees ? m.attendees : []).map((a) => a.name || a.email).filter(Boolean);
  if (names.length === 0) return '';
  const shown = names.slice(0, max);
  const rest = names.length - shown.length;
  return shown.join(', ') + (rest > 0 ? ` +${rest} weitere` : '');
}

// Einheitlicher Titel für jede Aufnahme: "Betreff - Datum Uhrzeit".
// Der Betreff kommt aus dem Outlook-Termin; ohne Termin vergibt ihn die KI nach der
// Zusammenfassung. Solange beides fehlt (noch nicht verarbeitet), steht dort "Meeting".
function recordingTitle(r) {
  const subject = r.title || 'Meeting';
  const d = new Date(r.mtime);
  const datum = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const zeit = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${subject} - ${datum} ${zeit}`;
}

// Freitextsuche über Titel, Datum und Teilnehmer einer Aufnahme.
function matchesQuery(r, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    recordingTitle(r),
    r.owner || '',
    ...(r.meeting && r.meeting.attendees ? r.meeting.attendees : []).flatMap((a) => [
      a.name || '',
      a.email || '',
    ]),
    ...(r.meeting && r.meeting.categories ? r.meeting.categories : []),
  ]
    .join(' ')
    .toLowerCase();
  // Alle Suchwörter müssen vorkommen, Reihenfolge egal
  return q.split(/\s+/).every((word) => haystack.includes(word));
}

const errText = (err) => (err && err.message ? err.message : String(err));

// Text als Datei herunterladen – Ersatz für „Datei öffnen" der Desktop-Version,
// die den Explorer bemüht hat.
function downloadText(name, text) {
  const url = URL.createObjectURL(new Blob([text || ''], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export default function App() {
  const { accounts } = useMsal();
  const account = accounts[0] || null;

  const recRef = useRef(null);
  const [state, setState] = useState({ status: 'idle', elapsed: 0, systemAudio: false });
  const [recordings, setRecordings] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Kann dieser Browser überhaupt Systemton aufnehmen?
  const [capabilities, setCapabilities] = useState(null);

  // Administratoren sehen alle Aufnahmen, nicht nur die eigenen. Die Entscheidung
  // trifft der Server anhand des geprüften Tokens – hier wird sie nur angezeigt.
  const [isAdmin, setIsAdmin] = useState(false);

  // Outlook
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetingError, setMeetingError] = useState(null);
  const [meetingsOpen, setMeetingsOpen] = useState(false);

  const pickMeeting = (m) => {
    setSelectedMeeting(m);
    setMeetingsOpen(false);
  };

  // Live-Coach
  const [coach, setCoach] = useState(null);
  const [coachEnabled, setCoachEnabled] = useState(
    localStorage.getItem('coachEnabled') !== 'false'
  );
  const pip = useCoachPip();

  // Nachfrage, wenn im Auswahldialog kein Systemton freigegeben wurde
  const [micOnlyPrompt, setMicOnlyPrompt] = useState(false);

  // Gehaltene Bildschirmfreigabe. Der Browser zeigt seinen Auswahldialog bei jedem
  // getDisplayMedia-Aufruf – das lässt sich nicht abschalten. Behält man die Freigabe
  // dagegen, fragt er nur einmal und alle folgenden Aufnahmen starten sofort.
  const captureRef = useRef(null);
  const [captureHeld, setCaptureHeld] = useState(false);

  const releaseCapture = () => {
    if (captureRef.current) captureRef.current.release();
    captureRef.current = null;
    setCaptureHeld(false);
  };

  // Holt die Freigabe, falls noch keine gehalten wird. Sie bleibt anschließend
  // bestehen – auch über das Ende der Aufnahme hinaus –, damit die nächste Aufnahme
  // ohne Auswahldialog startet.
  const ensureCapture = async (allowMicOnly) => {
    if (captureRef.current) return captureRef.current;
    const cap = await acquireCapture({ allowMicOnly });
    // Beendet der Nutzer die Freigabe über die Browser-Leiste, ist sie weg –
    // dann auch hier vergessen, sonst startet die nächste Aufnahme ins Leere.
    cap.onLost = () => {
      captureRef.current = null;
      setCaptureHeld(false);
    };
    captureRef.current = cap;
    setCaptureHeld(true);
    return cap;
  };

  const toggleCoach = (on) => {
    setCoachEnabled(on);
    localStorage.setItem('coachEnabled', String(on));
    if (recRef.current) recRef.current.coachEnabled = on;
    if (!on) setCoach(null);
  };

  // Individueller Coach-Prompt (pro Nutzer, jetzt serverseitig gespeichert)
  const [coachPrompt, setCoachPromptState] = useState('');
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptDraft, setPromptDraft] = useState('');

  const saveCoachPrompt = async () => {
    const val = promptDraft;
    setCoachPromptState(val);
    setPromptOpen(false);
    try {
      setSettings(await api.setCoachPrompt(val));
    } catch (err) {
      setError('Coach-Prompt konnte nicht gespeichert werden: ' + errText(err));
    }
  };

  // Einstellungen. Der HubSpot-Zugang gilt für alle gemeinsam und wird serverseitig
  // konfiguriert – hier lässt er sich nur prüfen, nicht ändern.
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(null); // 'test'
  const [settingsError, setSettingsError] = useState(null);
  const [settingsInfo, setSettingsInfo] = useState(null);

  const openSettings = async () => {
    const s = settings || (await api.getSettings());
    setSettings(s);
    setSettingsError(null);
    setSettingsInfo(null);
    setSettingsOpen(true);
  };

  const testSettings = async () => {
    setSettingsBusy('test');
    setSettingsError(null);
    setSettingsInfo(null);
    try {
      await api.testHubspotSettings();
      setSettingsInfo('Verbindung erfolgreich – HubSpot antwortet.');
    } catch (err) {
      setSettingsError(errText(err));
    } finally {
      setSettingsBusy(null);
    }
  };

  const toggleDeleteAudio = async (on) => {
    setSettingsError(null);
    setSettingsInfo(null);
    try {
      setSettings(await api.saveStorageSettings({ deleteAudioAfterTranscript: on }));
    } catch (err) {
      setSettingsError(errText(err));
    }
  };

  // Auto-Pipeline nach Aufnahme
  const [processing, setProcessing] = useState(null); // { id, step }
  const [pipelineError, setPipelineError] = useState(null);
  // Ruhiger Hinweis für Fälle, die kein Fehler sind (z. B. Aufnahme ohne Gespräch)
  const [pipelineNote, setPipelineNote] = useState(null);

  // Detailseite
  const [detailFor, setDetailFor] = useState(null);
  const [transcriptText, setTranscriptText] = useState(null);
  // Grund, falls Transkript/Protokoll nicht geladen werden konnten. Ohne diese
  // Unterscheidung sieht ein Ladefehler aus wie eine Aufnahme ohne Transkript.
  const [textsError, setTextsError] = useState(null);
  const [summaryText, setSummaryText] = useState(null);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  // Läuft beim Öffnen der Detailseite: Teilnehmer suchen und Protokoll übertragen
  const [autoPushBusy, setAutoPushBusy] = useState(false);
  // Sprecherzuordnung ist eingeklappt, bis jemand sie ausdrücklich aufruft
  const [speakersOpen, setSpeakersOpen] = useState(false);
  const [skipBusy, setSkipBusy] = useState(false);

  // Diese Aufnahme braucht keinen HubSpot-Eintrag – etwa ein Gespräch mit jemandem,
  // der gar nicht im CRM steht. Danach verschwindet auch die Sprecher-Aufforderung.
  const skipHubspot = async () => {
    setSkipBusy(true);
    setDialogError(null);
    try {
      await api.setHubspotSkip(detailFor.id, true);
      setDialogInfo('Für diese Aufnahme ist kein HubSpot-Eintrag vorgesehen.');
      await refreshDetail(detailFor.id);
    } catch (err) {
      setDialogError(errText(err));
    } finally {
      setSkipBusy(false);
    }
  };
  const [dialogError, setDialogError] = useState(null);
  const [dialogInfo, setDialogInfo] = useState(null);

  const [transcribing, setTranscribing] = useState(null);
  const [summarizing, setSummarizing] = useState(null);

  // KI-Chat zum Transkript ("Ask") – schwebendes Fenster unten rechts
  const [chatFor, setChatFor] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const chatEndRef = useRef(null);

  const openChat = (rec) => {
    if (chatFor && chatFor.id === rec.id) return; // schon offen
    setChatFor(rec);
    setChat([]);
    setChatInput('');
  };
  const closeChat = () => setChatFor(null);

  // Suche über Titel, Datum und Teilnehmer – die Daten liegen bereits im Browser,
  // deshalb wird hier nur gefiltert.
  const [listQuery, setListQuery] = useState('');
  // Suche im Protokolltext – läuft in der Datenbank, weil Protokolle nicht Teil der
  // Übersicht sind. Der angewandte Wert ist entprellt.
  const [summaryQuery, setSummaryQuery] = useState('');
  const [appliedSummaryQuery, setAppliedSummaryQuery] = useState('');
  const [summarySearchBusy, setSummarySearchBusy] = useState(false);

  const refresh = async (summaryFilter = appliedSummaryQuery) => {
    const list = await api.listRecordings(summaryFilter);
    setRecordings(list);
    return list;
  };

  const loadMeetings = async () => {
    setMeetingError(null);
    try {
      const today = await getTodaysMeetings();
      setMeetings(today);
      setSelectedMeeting(findCurrentMeeting(today));
    } catch (err) {
      setMeetingError(errText(err));
    }
  };

  useEffect(() => {
    checkCapabilities().then(setCapabilities);
    api.getMe().then((me) => setIsAdmin(Boolean(me.isAdmin))).catch(() => {});
    refresh().catch((err) => setError(errText(err)));
    api
      .getSettings()
      .then((s) => {
        setSettings(s);
        setCoachPromptState(s.coachPrompt || '');
      })
      .catch(() => {});
    loadMeetings();
  }, []);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chat, chatBusy]);

  // Protokollsuche entprellt an den Server schicken
  useEffect(() => {
    const q = summaryQuery.trim();
    if (q === appliedSummaryQuery) return;
    setSummarySearchBusy(true);
    const t = setTimeout(async () => {
      try {
        await refresh(q);
        setAppliedSummaryQuery(q);
      } catch (err) {
        setError(errText(err));
      } finally {
        setSummarySearchBusy(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [summaryQuery, appliedSummaryQuery]);

  // Terminliste schließen, wenn daneben geklickt wird
  useEffect(() => {
    if (!meetingsOpen) return;
    const close = (e) => {
      if (!e.target.closest || !e.target.closest('.meeting-picker')) setMeetingsOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [meetingsOpen]);

  // Auto-Suche in HubSpot beim Tippen (entprellt)
  useEffect(() => {
    if (!detailFor) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearchBusy(false);
      return;
    }
    setSearchBusy(true);
    const t = setTimeout(async () => {
      try {
        setResults(await api.searchHubspotContacts(q));
      } catch (err) {
        setResults([]);
        setDialogError(errText(err));
      } finally {
        setSearchBusy(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [query, detailFor]);

  const recording = state.status !== 'idle';

  // Präfix für Links in die HubSpot-Oberfläche; der Server ermittelt Region und
  // Portal-ID bei HubSpot selbst. Ohne eingerichteten Zugang bleibt es null.
  const hubspotContactUrl = (settings && settings.hubspot.contactUrlPrefix) || null;

  // Die Protokollsuche hat der Server schon angewandt; hier bleibt die Freitextsuche
  // über Titel, Datum und Teilnehmer.
  const visibleRecordings = recordings.filter((r) => matchesQuery(r, listQuery));

  const beginRecording = async ({ allowMicOnly }) => {
    setError(null);
    setPipelineError(null);
    setCoach(null);
    setBusy(true);
    const r = new MeetingRecorder(setState, setCoach);
    r.onSystemAudioLost = () =>
      setError('Die Bildschirmfreigabe wurde beendet – der Systemton fehlt ab jetzt.');
    try {
      // Beim ersten Start erscheint hier der Auswahldialog. Danach wird die Freigabe
      // gehalten, sodass alle weiteren Aufnahmen sofort loslaufen.
      const capture = await ensureCapture(allowMicOnly);
      await r.start({ meeting: selectedMeeting, allowMicOnly, coachEnabled, capture });
      recRef.current = r;

      // Coach-Fenster automatisch öffnen. Klappt ab der zweiten Aufnahme zuverlässig;
      // beim ersten Mal hat der Auswahldialog die Nutzerinteraktion bereits
      // verbraucht, die der Browser dafür verlangt. Schlägt es fehl, bleibt der
      // Knopf in der Aufnahmeleiste stehen.
      if (coachEnabled && pip.supported && !pip.pipWindow) {
        pip.open().catch(() => {});
      }
    } catch (err) {
      if (err instanceof NoSystemAudioError) {
        // Kein Fehler im engeren Sinn: der Nutzer hat „Audio teilen" vergessen.
        // Bewusst nachfragen, statt still nur das Mikrofon aufzunehmen.
        setMicOnlyPrompt(true);
      } else {
        setError('Aufnahme konnte nicht gestartet werden: ' + errText(err));
      }
      setState({ status: 'idle', elapsed: 0, systemAudio: false });
    } finally {
      setBusy(false);
    }
  };

  const start = () => {
    if (recRef.current || busy) return;
    beginRecording({ allowMicOnly: false });
  };

  const startMicOnly = () => {
    setMicOnlyPrompt(false);
    beginRecording({ allowMicOnly: true });
  };

  // Stoppen -> automatisch transkribieren + zusammenfassen
  const stop = async () => {
    const r = recRef.current;
    if (!r) return;
    recRef.current = null;
    let finished = null;
    try {
      finished = await r.stop();
    } catch (err) {
      setError('Aufnahme konnte nicht sauber beendet werden: ' + errText(err));
    }
    setCoach(null);
    pip.close();
    await refresh();
    if (finished) autoProcess(finished);
  };

  const autoProcess = async (rec) => {
    setPipelineError(null);
    setPipelineNote(null);
    try {
      setProcessing({ id: rec.id, step: 'Transkribiere …' });
      const result = await api.transcribeRecording(rec.id);
      await refresh();

      // Enthielt die Aufnahme kein Gespräch (zu kurz, stumm), gibt es nichts zu
      // transkribieren und erst recht nichts zusammenzufassen. Das ist kein Fehler,
      // deshalb nur ein ruhiger Hinweis statt einer roten Meldung.
      if (result && result.noSpeech) {
        setPipelineNote(
          'In dieser Aufnahme wurde kein Gespräch erkannt – sie ist vermutlich zu kurz ' +
            'oder stumm. Die Audiodatei bleibt erhalten und lässt sich erneut transkribieren.'
        );
        return;
      }

      setProcessing({ id: rec.id, step: 'Fasse zusammen …' });
      const done = await api.summarizeRecording(rec.id);

      // Der Server trägt das Protokoll gleich in HubSpot ein, wenn sich Teilnehmer
      // zuordnen lassen. Das Ergebnis hier melden, damit niemand rätselt.
      const hs = done && done.hubspot;
      if (hs && hs.needsSpeakers) {
        // Kein Fehler, aber auch nicht erledigt: Ohne Namen darf nichts ins CRM.
        setPipelineError(
          'Die Sprecher dieser Aufnahme sind nicht namentlich erkannt – im Transkript ' +
            'steht nur „Sprecher 0", „Sprecher 1". Solange das so ist, wird nichts nach ' +
            'HubSpot übertragen. Bitte die Aufnahme öffnen und die Sprecher zuordnen.'
        );
      } else if (hs && hs.pushed) {
        setPipelineNote(
          `Protokoll in HubSpot eingetragen bei: ${hs.contacts.join(', ')}.`
        );
      } else if (hs && hs.error) {
        setPipelineError(
          'HubSpot: ' + hs.error + ' – die Zusammenfassung ist gespeichert und lässt ' +
            'sich in der Detailansicht von Hand übertragen.'
        );
      } else if (hs && hs.skipped && hs.skipped.startsWith('keine Teilnehmer')) {
        setPipelineNote(
          `Kein HubSpot-Eintrag: ${hs.skipped}. Kontakte lassen sich in der ` +
            'Detailansicht von Hand zuordnen.'
        );
      }
    } catch (err) {
      setPipelineError(`${rec.name}: ` + errText(err));
    } finally {
      setProcessing(null);
      await refresh();
    }
  };

  const doLogout = async () => {
    if (recRef.current) await stop();
    releaseCapture(); // Bildschirmfreigabe nicht über die Abmeldung hinaus halten
    await logout();
  };

  // Steuerbefehle aus dem schwebenden Coach-Fenster
  const handleCommand = (cmd) => {
    const r = recRef.current;
    if (!r) return;
    if (cmd === 'pause') r.pause();
    if (cmd === 'resume') r.resume();
    if (cmd === 'stop') stop();
  };

  // --- Detailseite ---
  // Transkript und Protokoll nachladen. Schlägt das fehl, wird der Grund festgehalten
  // statt so zu tun, als gäbe es keinen Text – sonst sieht ein Ladefehler aus wie eine
  // Aufnahme ohne Transkript.
  const loadTexts = async (id) => {
    setTextsError(null);
    try {
      const texts = await api.getTexts(id);
      setTranscriptText(texts.transcript);
      setSummaryText(texts.summary);
    } catch (err) {
      setTextsError(errText(err));
    }
  };

  const openDetail = async (rec) => {
    setDetailFor(rec);
    setSpeakersOpen(false);
    setSelected(rec.contacts || []);
    setQuery('');
    setResults([]);
    setDialogError(null);
    setDialogInfo(null);
    setTranscriptText(null);
    setSummaryText(null);
    await loadTexts(rec.id);

    // Aufnahmen, die vor der Automatik entstanden sind, hier nachziehen: Teilnehmer
    // in HubSpot suchen und das Protokoll eintragen. Neue Aufnahmen sind zu diesem
    // Zeitpunkt längst übertragen, dann passiert hier nichts.
    // Fehlen die Sprechernamen, wird bewusst nichts übertragen – dann steht oben
    // die Zuordnung, und die stößt die Übertragung selbst an.
    if (rec.hasSummary && !rec.hubspotMeetingId && !rec.needsSpeakers) {
      setAutoPushBusy(true);
      try {
        const r = await api.autoPushToHubspot(rec.id);
        if (r && r.pushed) {
          setDialogInfo(`Protokoll in HubSpot eingetragen bei: ${r.contacts.join(', ')}.`);
          await refreshDetail(rec.id);
        } else if (r && r.error) {
          setDialogError('HubSpot: ' + r.error);
        }
      } catch (err) {
        setDialogError('HubSpot: ' + errText(err));
      } finally {
        setAutoPushBusy(false);
      }
    }
  };

  const askQuestion = async (e) => {
    e.preventDefault();
    const q = chatInput.trim();
    if (!q || chatBusy || !chatFor) return;
    const history = [...chat, { role: 'user', content: q }];
    setChat(history);
    setChatInput('');
    setChatBusy(true);
    try {
      const { answer } = await api.askTranscript(chatFor.id, history);
      setChat([...history, { role: 'assistant', content: answer }]);
    } catch (err) {
      setChat([...history, { role: 'assistant', content: '⚠ ' + errText(err), error: true }]);
    } finally {
      setChatBusy(false);
    }
  };

  const closeDetail = () => setDetailFor(null);

  const refreshDetail = async (id) => {
    const list = await refresh();
    const updated = list.find((x) => x.id === id);
    if (updated) {
      setDetailFor((cur) => (cur && cur.id === id ? updated : cur));
      await loadTexts(id);
    }
  };

  const doTranscribe = async (rec) => {
    setTranscribing(rec.id);
    setDialogError(null);
    try {
      await api.transcribeRecording(rec.id);
      await refreshDetail(rec.id);
    } catch (err) {
      setDialogError('Transkription: ' + errText(err));
    } finally {
      setTranscribing(null);
    }
  };

  const doSummarize = async (rec) => {
    setSummarizing(rec.id);
    setDialogError(null);
    try {
      await api.summarizeRecording(rec.id);
      await refreshDetail(rec.id);
    } catch (err) {
      setDialogError('Zusammenfassung: ' + errText(err));
    } finally {
      setSummarizing(null);
    }
  };

  const doPush = async () => {
    if (!detailFor) return;
    setPushBusy(true);
    setDialogError(null);
    setDialogInfo(null);
    try {
      // Auswahl mitschicken: manuell erfasste Kontakte haben noch keine HubSpot-ID
      // und können deshalb nicht vorab gespeichert werden.
      await api.pushToHubspot(detailFor.id, selected);
      await refreshDetail(detailFor.id);
      setDialogInfo('Meeting-Eintrag mit Zusammenfassung wurde in HubSpot angelegt.');
    } catch (err) {
      setDialogError('HubSpot: ' + errText(err));
    } finally {
      setPushBusy(false);
    }
  };

  const emailValid = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || '').trim());

  // Kontakt manuell per E-Mail hinzufügen (ohne HubSpot-ID -> wird beim Push
  // in HubSpot gesucht oder neu angelegt)
  const addManualEmail = () => {
    const mail = query.trim().toLowerCase();
    if (!emailValid(mail)) return;
    if (!selected.some((s) => (s.email || '').toLowerCase() === mail)) {
      setSelected([...selected, { id: null, email: mail, name: mail, company: '', manual: true }]);
    }
    setQuery('');
    setResults([]);
  };

  const saveContacts = async () => {
    try {
      await api.setRecordingContacts(detailFor.id, selected);
      await refreshDetail(detailFor.id);
      setDialogInfo('Kontakte gespeichert.');
    } catch (err) {
      setDialogError(errText(err));
    }
  };

  // ---------- Render ----------
  const chatWidget = chatFor && (
    <div className="chat-widget">
      <div className="chat-widget-head">
        <div className="chat-widget-title">
          <span className="chat-widget-badge">Ask KI</span>
          <span className="chat-widget-sub">
            {chatFor.title || chatFor.name}
          </span>
        </div>
        <button className="icon-btn" onClick={closeChat} title="Schließen">×</button>
      </div>
      <div className="chat">
        {chat.length === 0 && (
          <div className="chat-empty">
            Stelle Fragen zum Transkript – z. B. „Was wurde zum Preis besprochen?",
            „Welche Aufgaben habe ich übernommen?", „Fasse die Einwände zusammen."
          </div>
        )}
        {chat.map((m, i) => (
          <div key={i} className={`chat-msg ${m.role}${m.error ? ' error' : ''}`}>
            {m.role === 'assistant' && !m.error ? (
              <div dangerouslySetInnerHTML={{ __html: mdToHtml(m.content) }} />
            ) : (
              m.content
            )}
          </div>
        ))}
        {chatBusy && <div className="chat-msg assistant loading">Denkt nach …</div>}
        <div ref={chatEndRef} />
      </div>
      <form className="chat-input-row" onSubmit={askQuestion}>
        <input
          autoFocus
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="Frage zum Transkript …"
        />
        <button className="btn primary small" type="submit" disabled={chatBusy || !chatInput.trim()}>
          Senden
        </button>
      </form>
    </div>
  );

  // Coach-Inhalt ins schwebende Fenster spiegeln
  const pipContent =
    pip.pipWindow &&
    createPortal(
      <CoachOverlay
        state={state}
        coach={coach}
        onCommand={handleCommand}
        pipWindow={pip.pipWindow}
      />,
      pip.pipWindow.document.body
    );

  // ---------- Detailseite ----------
  if (detailFor) {
    const m = detailFor.meeting;
    return (
      <div className="app detail-page">
        <div className="detail-topbar">
          <button className="btn ghost small" onClick={closeDetail}>← Zurück</button>
          <div className="detail-title">
            <h1>{recordingTitle(detailFor)}</h1>
            <div className="detail-meta">
              {formatDuration(detailFor.durationMs)}
              {m && m.location ? ` · ${m.location}` : ''}
              {detailFor.hasAudio ? ` · ${formatSize(detailFor.size)}` : ''}
            </div>
          </div>
          <div className="detail-topbar-actions">
            {/* "Ask KI" sitzt als Chat-Knopf unten rechts und ist damit immer sichtbar,
                statt erst über die Kopfzeile geöffnet werden zu müssen. */}
            {/* Der Übertragen-Knopf sitzt unten bei den HubSpot-Kontakten, weil er
                erst sinnvoll ist, wenn dort Kontakte gewählt wurden. Oben bleibt
                nur die Statusanzeige. */}
            {detailFor.hubspotMeetingId && <span className="badge ok">✓ In HubSpot</span>}
          </div>
        </div>

        {(dialogError || dialogInfo) && (
          <div className="detail-messages">
            {dialogError && <p className="error">{dialogError}</p>}
            {dialogInfo && <p className="success">{dialogInfo}</p>}
          </div>
        )}

        {/* Ein schmaler Hinweis, mehr nicht. Die Zuordnung selbst steht unten bei den
            HubSpot-Kontakten – dorthin gehört sie, und nur dort wird sie gebraucht.
            Protokoll und Transkript bleiben oben und ungestört sichtbar. */}
        {detailFor.needsSpeakers && (
          <div className="speaker-banner">
            <span className="badge warn">Sprecher offen</span>
            <span>
              Die Namen fehlen noch, deshalb wurde nichts nach HubSpot übertragen.
              Transkript und Protokoll sind davon nicht betroffen.
            </span>
            <button className="btn ghost small" onClick={() => setSpeakersOpen(true)}>
              Zuordnen
            </button>
            <button className="btn ghost small" onClick={skipHubspot} disabled={skipBusy}>
              {skipBusy ? 'Speichere …' : 'Kein HubSpot nötig'}
            </button>
          </div>
        )}

        <div className="detail-grid">
          {/* Links: Protokoll */}
          <section className="detail-pane">
            <div className="pane-head">
              <h2>
                Protokoll
                <Info
                  text={
                    'Aus dem Transkript erzeugtes Ergebnisprotokoll: Themen als Abschnitte, ' +
                    'Entscheidungen und offene Punkte gekennzeichnet, am Ende die To-dos. ' +
                    'Nur dieses Protokoll wird nach HubSpot übertragen – nie das Transkript.'
                  }
                />
              </h2>
              {detailFor.hasSummary ? (
                <button
                  className="btn ghost small"
                  onClick={() => doSummarize(detailFor)}
                  disabled={summarizing === detailFor.id}
                >
                  {summarizing === detailFor.id ? 'Erstelle neu …' : 'Neu erstellen'}
                </button>
              ) : (
                <button
                  className="btn small"
                  disabled={!detailFor.hasTranscript || summarizing === detailFor.id}
                  onClick={() => doSummarize(detailFor)}
                  title={!detailFor.hasTranscript ? 'Erst transkribieren' : ''}
                >
                  {summarizing === detailFor.id ? 'Fasse zusammen …' : 'Zusammenfassen'}
                </button>
              )}
            </div>
            <div className="pane-body">
              {summaryText != null ? (
                <div className="prose" dangerouslySetInnerHTML={{ __html: mdToHtml(summaryText) }} />
              ) : (
                <p className="empty-small">
                  {detailFor.hasTranscript
                    ? 'Noch keine Zusammenfassung erstellt.'
                    : 'Erst nach der Transkription möglich.'}
                </p>
              )}
            </div>
          </section>

          {/* Rechts: Transkript */}
          <section className="detail-pane">
            <div className="pane-head">
              <h2>
                Transkript
                <Info
                  text={
                    'Wortwörtliche Mitschrift mit Sprechertrennung, erzeugt von Azure Speech. ' +
                    'Grundlage für Protokoll und für „Ask KI". Verlässt die App nicht: ' +
                    'Nach HubSpot geht ausschließlich das Protokoll.'
                  }
                />
              </h2>
              {detailFor.hasTranscript ? (
                <button
                  className="btn ghost small"
                  onClick={() =>
                    downloadText(detailFor.name.replace(/\.webm$/i, '.txt'), transcriptText)
                  }
                >
                  Herunterladen
                </button>
              ) : (
                <button
                  className="btn small"
                  disabled={transcribing === detailFor.id}
                  onClick={() => doTranscribe(detailFor)}
                >
                  {transcribing === detailFor.id ? 'Transkribiere …' : 'Transkribieren'}
                </button>
              )}
            </div>
            <div className="pane-body">
              {/* Drei verschiedene Zustände, die vorher alle zu "Noch kein Transkript
                  vorhanden" wurden – auch dann, wenn längst eines existierte und nur
                  das Laden fehlgeschlagen war. Genau das sah aus wie ein Transkript,
                  das mal da ist und mal nicht. */}
              {transcriptText != null ? (
                <pre className="transcript">{transcriptText || '(leer)'}</pre>
              ) : textsError ? (
                <p className="error">
                  Transkript konnte nicht geladen werden: {textsError}{' '}
                  <button className="btn ghost small" onClick={() => loadTexts(detailFor.id)}>
                    Erneut versuchen
                  </button>
                </p>
              ) : detailFor.hasTranscript ? (
                <p className="empty-small">Wird geladen …</p>
              ) : (
                <p className="empty-small">Noch kein Transkript vorhanden.</p>
              )}
            </div>
          </section>
        </div>

        {/* Unten: Termin und HubSpot */}
        <div className="detail-bottom">
          <section className="detail-pane">
            <div className="pane-head">
              <h2>
                Termin &amp; Teilnehmer
                <Info
                  text={
                    'Der Outlook-Termin, der bei der Aufnahme ausgewählt war – mit ' +
                    'Teilnehmerliste und Einladungstext. Aus diesen Teilnehmern werden die ' +
                    'HubSpot-Kontakte ermittelt.'
                  }
                />
              </h2>
            </div>
            <div className="pane-body">
              {m ? (
                <>
                  <div className="chips">
                    {(m.attendees || []).map((a) => (
                      <span className="chip subtle" key={a.email} title={a.email}>
                        {a.name || a.email}
                      </span>
                    ))}
                  </div>
                  {m.body && <pre className="meeting-body">{m.body}</pre>}
                </>
              ) : (
                <span className="empty-small">Kein Outlook-Termin zugeordnet.</span>
              )}
            </div>
          </section>

          <section className="detail-pane">
            <div className="pane-head">
              <h2>
                HubSpot-Kontakte
                <Info
                  align="right"
                  text={
                    'Die Termin-Teilnehmer werden automatisch in HubSpot gesucht und das ' +
                    'Protokoll dort als Meeting-Eintrag hinterlegt – ohne Zutun. Es wird ' +
                    'niemand neu angelegt; nur wer in HubSpot schon existiert, wird ' +
                    'verknüpft. Ein Klick auf einen Namen öffnet den Kontakt in HubSpot. ' +
                    'Fehlt jemand, lässt er sich unten suchen und mit „Speichern" ergänzen.'
                  }
                />
              </h2>
              <div className="detail-head-actions">
                {/* "Teilnehmer übernehmen" gibt es nicht mehr – die Zuordnung passiert
                    automatisch, sobald das Protokoll fertig ist. Speichern bleibt für
                    von Hand ergänzte Kontakte. */}
                <button className="btn ghost small" onClick={saveContacts}>Speichern</button>
              </div>
            </div>
            <div className="pane-body">
              <div className="chips">
                {selected.length === 0 && (
                  <span className="empty-small">
                    {autoPushBusy ? 'Suche Teilnehmer in HubSpot …' : 'Noch keine Kontakte zugeordnet'}
                  </span>
                )}
                {selected.map((c, i) => {
                  // Kontakte mit HubSpot-ID direkt dort öffnen. Manuell per E-Mail
                  // erfasste haben noch keine ID – die entsteht erst beim Übertragen.
                  const url = c.id && hubspotContactUrl ? hubspotContactUrl + c.id : null;
                  return (
                    <span
                      className={`chip ${c.manual ? 'manual' : ''}`}
                      key={c.id || c.email || i}
                      title={url ? `${c.email} · in HubSpot öffnen` : c.email}
                    >
                      {url ? (
                        <a
                          className="chip-link"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {c.name}
                        </a>
                      ) : (
                        c.name
                      )}
                      {c.manual && <span className="chip-tag">neu</span>}
                      <button onClick={() => setSelected(selected.filter((s) => s !== c))}>×</button>
                    </span>
                  );
                })}
              </div>
              <div className="search-row">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && emailValid(query) && results.length === 0) addManualEmail();
                  }}
                  placeholder="Kontakt in HubSpot suchen (Name, E-Mail, Firma) …"
                />
                {searchBusy && <span className="search-status">Suche …</span>}
              </div>
              {results.length > 0 && (
                <div className="results">
                  {results.map((c) => {
                    const already = selected.some((s) => s.id === c.id);
                    return (
                      <div className="result" key={c.id}>
                        <div>
                          <div>{c.name}</div>
                          <div className="result-sub">
                            {[c.email, c.company].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <button
                          className="btn ghost small"
                          disabled={already}
                          onClick={() => setSelected([...selected, c])}
                        >
                          {already ? 'Zugeordnet' : 'Hinzufügen'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {!searchBusy && query.trim().length >= 2 && results.length === 0 && (
                <div className="no-results">
                  {emailValid(query) ? (
                    <>
                      <span>Kein Kontakt in HubSpot gefunden.</span>
                      <button className="btn small" onClick={addManualEmail}>
                        „{query.trim()}" als neuen Kontakt anlegen
                      </button>
                    </>
                  ) : (
                    <span>Kein Treffer. Für einen neuen Kontakt eine vollständige E-Mail-Adresse eingeben.</span>
                  )}
                </div>
              )}

              {/* Sprecherzuordnung sitzt hier, weil sie nur für HubSpot gebraucht wird.
                  Eingeklappt, damit sie niemandem den Weg verstellt, der nur das
                  Transkript lesen will. */}
              {detailFor.needsSpeakers && (
                <div className="speaker-block">
                  {speakersOpen ? (
                    <Speakers
                      recording={detailFor}
                      onDone={async (r) => {
                        const hs = r && r.hubspot;
                        if (hs && hs.pushed) {
                          setDialogInfo(
                            `Protokoll mit Namen neu erstellt und in HubSpot eingetragen bei: ${hs.contacts.join(', ')}.`
                          );
                        } else if (hs && hs.error) {
                          setDialogError('HubSpot: ' + hs.error);
                        } else {
                          setDialogInfo('Protokoll mit den Namen neu erstellt.');
                        }
                        setSpeakersOpen(false);
                        await refreshDetail(detailFor.id);
                      }}
                    />
                  ) : (
                    <button className="btn ghost small" onClick={() => setSpeakersOpen(true)}>
                      Sprecher zuordnen – nötig für die Übertragung
                    </button>
                  )}
                </div>
              )}

              {/* Übertragen direkt hier, wo die Kontakte gewählt werden – so ist der
                  Weg von der Auswahl zur Übertragung ein Schritt statt zweier. */}
              <div className="pane-actions">
                {detailFor.hubspotMeetingId ? (
                  // Nicht nur "erledigt", sondern wann und an wen – sonst muss man in
                  // HubSpot nachsehen, um das zu erfahren.
                  <div className="hubspot-status">
                    <span className="badge ok">✓ In HubSpot</span>
                    <span className="hubspot-status-text">
                      {detailFor.hubspotSyncedAt
                        ? `Übertragen am ${formatDate(new Date(detailFor.hubspotSyncedAt).getTime())}`
                        : 'Übertragen'}
                      {(detailFor.contacts || []).length > 0 && (
                        <>
                          {' an '}
                          {(detailFor.contacts || []).map((c, i) => {
                            const url = c.id && hubspotContactUrl ? hubspotContactUrl + c.id : null;
                            return (
                              <span key={c.id || c.email || i}>
                                {i > 0 && ', '}
                                {url ? (
                                  <a
                                    className="chip-link"
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={c.email}
                                  >
                                    {c.name}
                                  </a>
                                ) : (
                                  c.name
                                )}
                              </span>
                            );
                          })}
                        </>
                      )}
                    </span>
                  </div>
                ) : (
                  <button
                    className="btn primary"
                    onClick={doPush}
                    disabled={pushBusy || !detailFor.hasSummary || selected.length === 0}
                    title={
                      !detailFor.hasSummary
                        ? 'Erst Zusammenfassung erstellen'
                        : selected.length === 0
                          ? 'Erst Kontakte zuordnen'
                          : ''
                    }
                  >
                    {pushBusy ? 'Übertrage …' : 'Zusammenfassung nach HubSpot'}
                  </button>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* Dauerhaft sichtbarer Einstieg in den KI-Chat. Sobald er offen ist,
            nimmt das Chatfenster selbst diesen Platz ein. */}
        {detailFor.hasTranscript && !chatFor && (
          <button
            className="chat-fab"
            onClick={() => openChat(detailFor)}
            aria-label="Fragen zum Transkript stellen"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
            <span className="chat-fab-label">Fragen zum Meeting</span>
          </button>
        )}

        {chatWidget}
        {pipContent}
      </div>
    );
  }

  // ---------- Übersicht ----------
  return (
    <div className="app">
      <header>
        <div className="brand">
          <div className="logo-dot" />
          <div>
            <h1>Meeting Recorder</h1>
            <span className="sub">Aufnahme · Transkription · KI · HubSpot</span>
          </div>
        </div>
        <div className="user">
          <button
            className={`btn ghost small settings-btn ${settings && !settings.hubspot.configured ? 'attention' : ''}`}
            onClick={openSettings}
            title="Einstellungen (HubSpot-Zugang)"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Einstellungen
          </button>
          <div className="user-meta">
            <span className="user-name">{account ? account.name || account.username : ''}</span>
            <span className="user-mail">{account ? account.username : ''}</span>
          </div>
          <button className="btn ghost small" onClick={doLogout}>Abmelden</button>
        </div>
      </header>

      {/* Browser-/Systemvoraussetzungen */}
      {capabilities && !capabilities.ok && (
        <section className="control-card">
          <p className="error">
            <strong>{capabilities.title}:</strong> {capabilities.message}
          </p>
        </section>
      )}

      {/* Aufnahme */}
      <section className={`control-card ${recording ? 'live' : ''}`}>
        <div className="control-main">
          {!recording ? (
            <>
              <button
                className="record-btn"
                onClick={start}
                disabled={busy || (capabilities && !capabilities.ok)}
                title="Aufnahme starten"
              >
                <span className="rec-ring" />
              </button>
              <div className="control-text">
                <div className="control-title">
                  {busy ? 'Starte …' : 'Aufnahme starten'}
                  <Info
                    text={
                      'Beim ersten Start fragt der Browser, was geteilt werden soll. ' +
                      'Wähle „Gesamter Bildschirm" und hake „Audio teilen" an – nur dann ' +
                      'wird der Gesprächspartner mit aufgenommen, sonst nur dein Mikrofon. ' +
                      'Die Freigabe bleibt danach bestehen, alle weiteren Aufnahmen starten ' +
                      'sofort. Nach dem Stoppen entstehen Transkript und Protokoll automatisch.'
                    }
                  />
                </div>
                <div className="control-sub">
                  {captureHeld
                    ? 'Bildschirm ist freigegeben · startet sofort, ohne Rückfrage'
                    : 'Mikrofon + Systemton · Beim ersten Start „Gesamter Bildschirm" wählen und „Audio teilen" anhaken'}
                </div>
              </div>
              {captureHeld && (
                <div className="control-buttons">
                  <button
                    className="btn ghost"
                    onClick={releaseCapture}
                    title="Bildschirmfreigabe beenden – der nächste Start fragt wieder nach"
                  >
                    Freigabe beenden
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <span className={`live-dot ${state.status === 'paused' ? 'paused' : ''}`} />
              <div className="control-text">
                <div className="timer">{formatDuration(state.elapsed)}</div>
                <div className="control-sub">
                  {state.status === 'paused' ? 'Pausiert' : 'Aufnahme läuft'}
                  {!state.systemAudio && ' · nur Mikrofon'}
                  {selectedMeeting && ` · ${selectedMeeting.subject}`}
                </div>
              </div>
              <div className="control-buttons">
                {pip.supported && !pip.pipWindow && (
                  <button className="btn ghost" onClick={pip.open} title="Coach über allen Fenstern anzeigen">
                    Coach-Fenster
                  </button>
                )}
                {state.status === 'paused' ? (
                  <button className="btn" onClick={() => recRef.current?.resume()}>Fortsetzen</button>
                ) : (
                  <button className="btn" onClick={() => recRef.current?.pause()}>Pause</button>
                )}
                <button className="btn danger" onClick={stop}>Stopp</button>
              </div>
            </>
          )}
        </div>

        {/* Live-Coach */}
        {recording && coachEnabled && (
          <div className="coach-panel">
            <div className="coach-head">
              <span className="coach-badge">KI-Live-Coach</span>
              {coach && (
                <span className="coach-time">
                  {new Date(coach.at).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </span>
              )}
            </div>
            <div className="coach-text">
              {coach ? coach.tip : 'Höre zu … erste Empfehlung folgt nach den ersten Gesprächsminuten.'}
            </div>
          </div>
        )}

        {/* Termin */}
        <div className="meeting-row">
          <span className="meeting-label">
            Outlook-Termin
            <Info
              text={
                'Vorausgewählt ist der gerade laufende Termin, sonst der nächste des ' +
                'Tages. Der gewählte Termin liefert Betreff und Teilnehmer – und die ' +
                'Teilnehmer werden später automatisch in HubSpot gesucht. Ohne Termin ' +
                'vergibt die KI nach dem Meeting selbst einen Titel.'
              }
            />
          </span>
          {/* Eigene Auswahlliste statt <select>: Ein <option> kann nur eine Zeile
              reinen Text anzeigen – Teilnehmer und Kategorie brauchen mehr Platz. */}
          <div className="meeting-picker">
            <button
              type="button"
              className="meeting-picker-btn"
              onClick={() => setMeetingsOpen((o) => !o)}
              disabled={recording}
            >
              <span className="meeting-picker-value">
                {selectedMeeting
                  ? `${meetingTime(selectedMeeting)} · ${selectedMeeting.subject}`
                  : '— Kein Termin —'}
              </span>
              <span className="meeting-picker-chevron">▾</span>
            </button>

            {meetingsOpen && !recording && (
              <div className="meeting-list">
                <button
                  type="button"
                  className={`meeting-option ${!selectedMeeting ? 'active' : ''}`}
                  onClick={() => pickMeeting(null)}
                >
                  <span className="meeting-option-title">— Kein Termin —</span>
                </button>

                {meetings.length === 0 && (
                  <div className="meeting-option-empty">Heute keine Termine gefunden.</div>
                )}

                {meetings.map((m) => {
                  const people = attendeeLine(m);
                  return (
                    <button
                      type="button"
                      className={`meeting-option ${
                        selectedMeeting && selectedMeeting.id === m.id ? 'active' : ''
                      }`}
                      key={m.id}
                      onClick={() => pickMeeting(m)}
                    >
                      <span className="meeting-option-title">
                        <span className="meeting-option-time">{meetingTime(m)}</span>
                        <span className="meeting-option-subject">{m.subject}</span>
                        {(m.categories || []).map((c) => (
                          <span className="chip-tag" key={c}>
                            {c}
                          </span>
                        ))}
                      </span>
                      {people && <span className="meeting-option-people">{people}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button className="btn ghost small" onClick={loadMeetings} disabled={recording}>
            Aktualisieren
          </button>
          {selectedMeeting && (
            <span className="meeting-count">
              {(selectedMeeting.attendees || []).length} Teilnehmer
            </span>
          )}
        </div>

        {/* Einstellungen */}
        <div className="settings-row">
          <label className="switch-label">
            <span className={`switch ${coachEnabled ? 'on' : ''}`} onClick={() => toggleCoach(!coachEnabled)}>
              <span className="switch-knob" />
            </span>
            <span>
              KI-Live-Coach
              <span className="switch-hint"> · alle 20 s Themen & Unklarheiten während der Aufnahme</span>
              <Info
                text={
                  'Während der Aufnahme wird alle 20 Sekunden ein kurzes Stück ' +
                  'mitgehört und daraus ein Hinweis erzeugt: welche Themen gerade laufen ' +
                  'und wo etwas unklar geblieben ist. Der Hinweis erscheint in einem ' +
                  'kleinen Fenster, das über allen anderen Programmen schwebt. ' +
                  'Auf das fertige Protokoll hat der Coach keinen Einfluss.'
                }
              />
            </span>
          </label>
          <button
            className="btn ghost small"
            onClick={() => {
              setPromptDraft(coachPrompt);
              setPromptOpen(true);
            }}
          >
            Coach-Prompt anpassen{coachPrompt.trim() ? ' ✓' : ''}
          </button>
        </div>

        {captureHeld && !recording && (
          <p className="hint subtle">
            Die Bildschirmfreigabe bleibt bestehen, damit die nächste Aufnahme sofort
            startet. Chrome zeigt deshalb weiter an, dass der Bildschirm geteilt wird –
            aufgenommen wird aber erst ab „Aufnahme starten".
          </p>
        )}
        {recording && !state.systemAudio && (
          <p className="hint">Systemton nicht freigegeben – es wird nur das Mikrofon aufgenommen.</p>
        )}
        {state.uploadError && (
          <p className="error">
            Hochladen stockt: {state.uploadError} – die Aufnahme läuft weiter und wird erneut versucht.
          </p>
        )}
        {meetingError && <p className="hint subtle">Outlook: {meetingError}</p>}
        {error && <p className="error">{error}</p>}
      </section>

      {/* Aufnahmen */}
      <section className="list">
        <div className="list-head">
          <h2>
            {isAdmin ? 'Alle Aufnahmen' : 'Meine Aufnahmen'}{' '}
            <span className="count">
              {visibleRecordings.length}
              {visibleRecordings.length !== recordings.length ? ` / ${recordings.length}` : ''}
            </span>
          </h2>
          <button className="btn ghost small" onClick={() => refresh()}>Aktualisieren</button>
        </div>

        <div className="list-search">
          <label className="list-search-field">
            <span className="list-search-label">
              Aufnahme finden
              <Info
                text={
                  'Durchsucht Titel, Datum, Teilnehmer, Kategorien und den Ersteller. ' +
                  'Mehrere Wörter werden alle geprüft, die Reihenfolge spielt keine Rolle.'
                }
              />
            </span>
            <input
              value={listQuery}
              onChange={(e) => setListQuery(e.target.value)}
              placeholder="Titel, Datum oder Teilnehmer …"
              spellCheck={false}
            />
          </label>
          <label className="list-search-field">
            <span className="list-search-label">
              Im Protokoll suchen
              {summarySearchBusy && <span className="list-search-status"> · suche …</span>}
              <Info
                align="right"
                text={
                  'Sucht das Schlagwort im Text der Protokolle – also nach dem, was ' +
                  'inhaltlich besprochen wurde. Das läuft in der Datenbank, deshalb dauert ' +
                  'es einen Moment. Das Roh-Transkript wird dabei nicht durchsucht.'
                }
              />
            </span>
            <input
              value={summaryQuery}
              onChange={(e) => setSummaryQuery(e.target.value)}
              placeholder="Schlagwort im Protokolltext …"
              spellCheck={false}
            />
          </label>
        </div>

        {pipelineError && <p className="error">Automatische Verarbeitung: {pipelineError}</p>}
        {pipelineNote && <p className="hint subtle">{pipelineNote}</p>}

        {visibleRecordings.length === 0 ? (
          <div className="empty-state">
            {recordings.length === 0 && !appliedSummaryQuery ? (
              <>
                <p>Noch keine Aufnahmen.</p>
                <p className="empty-sub">
                  Starte oben eine Aufnahme – Transkript und Zusammenfassung entstehen danach
                  automatisch.
                </p>
              </>
            ) : (
              <>
                <p>Keine Aufnahme passt zur Suche.</p>
                <p className="empty-sub">
                  Suchbegriffe ändern oder die Felder leeren, um wieder alle zu sehen.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="rec-list">
            {visibleRecordings.map((r) => {
              const isProcessing = processing && processing.id === r.id;
              return (
                <div className="rec-item" key={r.id} onClick={() => openDetail(r)}>
                  <div className="rec-primary">
                    <div className="rec-title">{recordingTitle(r)}</div>
                    <div className="rec-meta">
                      {/* Ist die Audiodatei nach der Transkription gelöscht worden,
                          steht dort einfach nichts – das ist der Normalfall und
                          keine Meldung wert. */}
                      {formatDuration(r.durationMs)}
                      {r.hasAudio ? ` · ${formatSize(r.size)}` : ''}
                      {r.owner ? ` · ${r.owner}` : ''}
                    </div>
                    {attendeeLine(r.meeting, 5) && (
                      <div className="rec-people">{attendeeLine(r.meeting, 5)}</div>
                    )}
                  </div>
                  <div className="rec-status">
                    {isProcessing ? (
                      <span className="pill busy">
                        <span className="spinner" /> {processing.step}
                      </span>
                    ) : (
                      <>
                        <span className={`pill ${r.hasTranscript ? 'ok' : 'todo'}`}>
                          {r.hasTranscript ? '✓ ' : ''}Transkript
                        </span>
                        <span className={`pill ${r.hasSummary ? 'ok' : 'todo'}`}>
                          {r.hasSummary ? '✓ ' : ''}Zusammenfassung
                        </span>
                        {r.needsSpeakers && (
                          <span className="pill warn" title="Ohne Namen wird nichts nach HubSpot übertragen">
                            ⚠ Sprecher offen
                          </span>
                        )}
                        <span
                          className={`pill ${
                            r.hubspotMeetingId ? 'ok' : (r.contacts || []).length ? 'mid' : 'todo'
                          }`}
                        >
                          {r.hubspotMeetingId
                            ? '✓ HubSpot'
                            : (r.contacts || []).length
                              ? `${r.contacts.length} Kontakt(e)`
                              : 'HubSpot'}
                        </span>
                      </>
                    )}
                  </div>
                  {r.hasTranscript && (
                    <button
                      className={`ask-icon ${chatFor && chatFor.id === r.id ? 'active' : ''}`}
                      title="Fragen zum Transkript (KI-Chat)"
                      onClick={(e) => {
                        e.stopPropagation();
                        openChat(r);
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                      </svg>
                    </button>
                  )}
                  <span className="rec-chevron">›</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <footer>Microsoft Entra · Outlook · Azure Speech · gpt-5-mini · HubSpot</footer>

      {/* Nachfrage: kein Systemton freigegeben */}
      {micOnlyPrompt && (
        <div className="modal-backdrop" onClick={() => setMicOnlyPrompt(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h3>Kein Systemton freigegeben</h3>
                <p className="modal-sub">Ohne Systemton wird nur deine eigene Stimme aufgenommen</p>
              </div>
              <button className="icon-btn" onClick={() => setMicOnlyPrompt(false)} title="Schließen">×</button>
            </div>
            <div className="modal-body">
              <p className="prompt-help">
                Im Auswahlfenster von Chrome muss <strong>„Gesamter Bildschirm"</strong> gewählt und
                unten links <strong>„Audio teilen"</strong> angehakt werden. Nur dann wird der
                Gesprächspartner mit aufgenommen.
              </p>
              <p className="prompt-help">
                Auf dem Mac ist dafür macOS 14.2 oder neuer nötig. Beim ersten Mal fragt macOS nach
                der Berechtigung zur Bildschirmaufnahme für den Browser.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn ghost" onClick={startMicOnly}>
                Trotzdem nur Mikrofon aufnehmen
              </button>
              <div className="spacer" />
              <button
                className="btn primary"
                onClick={() => {
                  setMicOnlyPrompt(false);
                  start();
                }}
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Coach-Prompt anpassen */}
      {promptOpen && (
        <div className="modal-backdrop" onClick={() => setPromptOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h3>KI-Live-Coach anpassen</h3>
                <p className="modal-sub">Gilt für alle künftigen Aufnahmen · zu deinem Konto gespeichert</p>
              </div>
              <button className="icon-btn" onClick={() => setPromptOpen(false)} title="Schließen">×</button>
            </div>
            <div className="modal-body">
              <p className="prompt-help">
                Beschreibe, wie der Coach dich unterstützen soll, oder gib ihm Hintergrundwissen
                (z. B. Produktdetails, Preise, typische Einwände, worauf du im Gespräch achten willst).
                Das Format (kurze Themen- & Unklarheiten-Hinweise) bleibt erhalten.
              </p>
              <textarea
                className="prompt-area"
                autoFocus
                value={promptDraft}
                onChange={(e) => setPromptDraft(e.target.value)}
                placeholder={
                  'Beispiel:\n' +
                  'Wir verkaufen ein QM-System für Kliniken. Achte auf Budget, Audit-Termine und ' +
                  'Zuständigkeiten. Weise mich auf Einwände zu Datenschutz und Einführungsaufwand hin.'
                }
              />
            </div>
            <div className="modal-actions">
              <button className="btn ghost" onClick={() => setPromptDraft('')}>Leeren</button>
              <div className="spacer" />
              <button className="btn ghost" onClick={() => setPromptOpen(false)}>Abbrechen</button>
              <button className="btn primary" onClick={saveCoachPrompt}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {/* Einstellungen: HubSpot-Zugangsdaten pro Nutzer */}
      {settingsOpen && (
        <div className="modal-backdrop" onClick={() => setSettingsOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h3>Einstellungen</h3>
                <p className="modal-sub">HubSpot gilt für alle · Speicher-Einstellung nur für dich</p>
              </div>
              <button className="icon-btn" onClick={() => setSettingsOpen(false)} title="Schließen">×</button>
            </div>
            <div className="modal-body">
              <div className="settings-group">
                <div className="settings-group-head">
                  <h4>
                    HubSpot
                    <Info
                      text={
                        'Alle Nutzer arbeiten auf demselben HubSpot-Account. Der Zugang liegt ' +
                        'auf dem Server und wird von einem Administrator gepflegt – hier ist ' +
                        'nichts einzutragen. Ist er nicht eingerichtet, funktionieren Aufnahme, ' +
                        'Transkript und Protokoll trotzdem; nur die Übertragung entfällt.'
                      }
                    />
                  </h4>
                  <span className={`badge ${settings && settings.hubspot.configured ? 'ok' : 'warn'}`}>
                    {settings && settings.hubspot.configured ? 'eingerichtet' : 'nicht eingerichtet'}
                  </span>
                </div>

                <p className="field-hint">
                  {settings && settings.hubspot.configured ? (
                    <>
                      Alle Nutzer arbeiten auf demselben HubSpot-Account
                      (<code>{settings.hubspot.baseUrl}</code>). Der Zugang ist auf dem Server
                      hinterlegt – hier ist nichts einzutragen.
                    </>
                  ) : (
                    <>
                      Auf dem Server ist kein HubSpot-Token hinterlegt. Aufnahme, Transkript und
                      Zusammenfassung funktionieren trotzdem; nur die Übertragung nach HubSpot
                      schlägt fehl. Ein Administrator muss die Umgebungsvariable
                      <code>HUBSPOT_TOKEN</code> in den App-Einstellungen setzen.
                    </>
                  )}
                </p>

                {settingsError && <p className="error">{settingsError}</p>}
                {settingsInfo && <p className="success">{settingsInfo}</p>}
              </div>

              <div className="settings-group">
                <div className="settings-group-head">
                  <h4>
                    Speicher
                    <Info
                      text={
                        'Die Audiodatei ist nur ein Zwischenschritt zum Transkript. Sie wird ' +
                        'ausschließlich gelöscht, wenn das Transkript nachweislich gespeichert ' +
                        'ist – blieb die Aufnahme stumm oder ging etwas schief, bleibt sie ' +
                        'erhalten. Transkript und Protokoll bleiben in jedem Fall.'
                      }
                    />
                  </h4>
                </div>
                <label className="switch-label">
                  <span
                    className={`switch ${settings && settings.storage.deleteAudioAfterTranscript ? 'on' : ''}`}
                    onClick={() =>
                      toggleDeleteAudio(!(settings && settings.storage.deleteAudioAfterTranscript))
                    }
                  >
                    <span className="switch-knob" />
                  </span>
                  <span>
                    Audiodatei nach Transkription löschen
                    <span className="switch-hint">
                      {' '}· Transkript und Zusammenfassung bleiben erhalten · wird nur gelöscht,
                      wenn die Transkription Text geliefert hat
                    </span>
                  </span>
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <div className="spacer" />
              <button
                className="btn ghost"
                onClick={testSettings}
                disabled={settingsBusy !== null || !(settings && settings.hubspot.configured)}
              >
                {settingsBusy === 'test' ? 'Teste …' : 'HubSpot-Verbindung testen'}
              </button>
              <button className="btn primary" onClick={() => setSettingsOpen(false)}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {chatWidget}
      {pipContent}
    </div>
  );
}

export { login };
